<template>
  <Navbar :prop-active-menu="constants.ROUTING.VISUALIZATION"
          @on-switch-language="languageOnSwitch"
          :prop-feedback-from-section="constants.FEEDBACK.EXPLORATION"
          :prop-close-feedback="feedbackOffCanvas.close"
          :prop-show-sections="true"
          :prop-show-feedback="true"/>
  <div id="dashboardCalendar"></div>
  <main class="flex-shrink-0">
    <AboutButton
        @click="aboutButtonOnClick"
        prop-id="aboutButtonVisualization"
        :prop-show-tooltip="tooltips.about.status"
    />

    <!-- Information -->
    <ModalInformation
        :prop-modal-status="modals.about.status"
        :prop-show-section="modals.about.section"
        :prop-glossary-term="modals.about.term"
        :prop-show-about-startup="showAboutStartup"
        @on-modal-about-close="aboutModalOnClose"
    />

    <!-- Monitoring plot -->
    <PlotMonitoring
        :prop-form-data="plotFormData.monitoring"
        :prop-plot-layers="plotLayers.monitoring"
        :prop-plot-layer-names="plotLayerNames.monitoring"
        :prop-plot-layers-count="plotLayersCountTotal.monitoring"
        :prop-plot-layers-trigger="plotDataTrigger.monitoring"
        :prop-modal-status="modals.monitoring"
        @on-open-modal="modalPlotOnOpen('monitoring')"
        @on-close-modal-click="modalPlotOnClose('monitoring')"
        @on-time-series-data-error="timeSeriesDataOnError"
        @after-plot-data-loading="timeSeriesDataAfterLoading"
    />

    <!-- Forecast plot -->
    <PlotForecast
        :prop-form-data="plotFormData.forecast"
        :prop-plot-layers="plotLayers.forecast"
        :prop-plot-layer-names="plotLayerNames.forecast"
        :prop-plot-layers-count="plotLayersCountTotal.forecast"
        :prop-plot-layers-trigger="plotDataTrigger.forecast"
        :prop-modal-status="modals.forecast"
        @on-open-modal="modalPlotOnOpen('forecast')"
        @on-close-modal-click="modalPlotOnClose('forecast')"
        @on-time-series-data-error="timeSeriesDataOnError"
        @after-plot-data-loading="timeSeriesDataAfterLoading"
    />

    <!-- Projection plots -->
    <PlotProjection
        :prop-form-data="plotFormData.projection"
        :prop-plot-layers="plotLayers.projection"
        :prop-plot-layer-names="plotLayerNames.projection"
        :prop-plot-layers-count="plotLayersCountTotal.projection"
        :prop-locale="this.$i18n.locale"
        :prop-plot-layers-trigger="plotDataTrigger.projection"
        :prop-modal-status="modals.projection"
        @on-open-modal="modalPlotOnOpen('projection')"
        @on-close-modal-click="modalPlotOnClose('projection')"
        @on-time-series-data-error="timeSeriesDataOnError"
        @after-plot-data-loading="timeSeriesDataAfterLoading"
    />

    <!-- Map -->
    <MapView
        :prop-form-data="formData"
        :prop-locale="this.$i18n.locale"
        :prop-parent-loading="this.childrenLoading"
        :prop-plot-loading="this.plotLoading"
        @on-time-series-data-loading="timeSeriesDataOnLoading"
        @on-time-series-button-click-ready="timeSeriesButtonOnClickReady"
        @on-about-button-click="aboutButtonOnClick"
    />

    <Toast
        prop-id="toastSearchError"
        :prop-trigger="toastSearchErrorMessageTrigger"
        :prop-auto-hide=true
        prop-type="danger"
        @on-toast-hidden="toastSearchErrorMessageHide">
      <template v-slot:body>{{ toastSearchErrorMessage }}</template>
    </Toast>

    <Transition name="bounce">
      <div v-show="showFilterWindow" class="filter-component rounded mb-2 bg-opacity-90 pt-3">
        <div class="filter-group">
          <div class="row border-bottom">
            <div class="col-12 order-1 order-md-0 col-md-11">
              <nav class="nav nav-pills flex-column flex-md-row border-0 pb-1">
                <!-- Hard-coded buttons for components -->
                <button role="link" class="flex-sm-fill flex-lg-grow-0 nav-link fw-semibold tooltip-init"
                        :class="{
                            active: this.selectedComponent === this.constants.COMPONENTS.MONITORING,
                            disabled: this.childrenLoading,
                            'bg-secondary-subtle': this.selectedComponent === this.constants.COMPONENTS.MONITORING && this.childrenLoading === true
                        }"
                        :disabled="this.childrenLoading"
                        @click="componentSelect(this.constants.COMPONENTS.MONITORING)"
                        data-bs-toggle="tooltip"
                        :data-bs-title="tooltipGetI18NText('DiscoverComp')">
                  <i class="bi bi-search pe-2"></i>
                  {{ $t('SearchForm.monitoring') }}
                </button>
                <button role="link" class="flex-sm-fill flex-lg-grow-0 nav-link fw-semibold tooltip-init"
                        :class="{
                            active: selectedComponent === this.constants.COMPONENTS.FORECAST,
                            disabled: this.childrenLoading,
                            'bg-secondary-subtle': selectedComponent === this.constants.COMPONENTS.FORECAST && this.childrenLoading === true
                        }"
                        :disabled="this.childrenLoading"
                        @click="componentSelect(this.constants.COMPONENTS.FORECAST)"
                        data-bs-toggle="tooltip"
                        :data-bs-title="tooltipGetI18NText('ForecastComp')">
                  <i class="bi bi-cloud-fog2 pe-2"></i>{{ $t('SearchForm.forecast') }}
                </button>
                <button role="link" class="flex-sm-fill flex-lg-grow-0 nav-link fw-semibold tooltip-init"
                        :class="{
                            active: selectedComponent === this.constants.COMPONENTS.PROJECTION,
                            disabled: this.childrenLoading,
                            'bg-secondary-subtle': selectedComponent === this.constants.COMPONENTS.PROJECTION && this.childrenLoading === true
                        }"
                        :disabled="this.childrenLoading"
                        @click="componentSelect(this.constants.COMPONENTS.PROJECTION)"
                        data-bs-toggle="tooltip"
                        :data-bs-title="tooltipGetI18NText('ProjectionComp')">
                  <i class="bi bi-graph-up pe-2"></i>{{ $t('SearchForm.projection') }}
                </button>
              </nav>
            </div>
            <div class="col-12 order-0 order-md-1 col-md-1 justify-content-end pb-2 pb-md-0"
                 v-show="childrenLoading === false">
              <button type="button" class="btn-close float-end" aria-label="Close" @click="filterToggle"></button>
            </div>
          </div>

          <div class="container mt-3 mb-2 filter-component-content">
            <keep-alive>
              <component :is="currentComponent"
                         :prop-initialize-tooltips="true"
                         :prop-last-uuid="lastFormUUID"
                         :prop-block-component="blockChildrenComponents"
                         @onSearchClick="searchHandle"
                         @onSearchClickError="searchHandleError"
                         @onSearchClickWait="searchButtonOnClickWait"
                         @afterSearchClickResult="searchButtonAfterClickResult"
                         @tooltipGlossaryOnClick="tooltipGlossaryOnClick"
              />
            </keep-alive>
          </div>
        </div>
      </div>
    </Transition>

    <Transition>
      <button v-if="!showFilterWindow"
              @click="filterToggle"
              style="z-index: 1000"
              class="btn btn-lg btn-purple filter-button-close position-absolute bottom-0 start-50 translate-middle">
        <i class="bi bi-filter-square me-1" aria-hidden="true"/>
        {{ $t('SearchForm.show_data') }}
      </button>
    </Transition>
  </main>

  <!-- Cookies window -->
  <PrivacyCookies/>

  <!-- Pixel -->
  <Pixel :prop-source="constants.ROUTING.VISUALIZATION" v-if="flagUsePixel()===true"/>

</template>

<script>
import Storage from "@/libs/Storage";
import LoginStorage from '@/libs/Login';
import constants from "@/libs/Constants";
import Toast from "@/components/Toast.vue";
import Navbar from '@/components/Navbar.vue';
import MapView from '@/views/Visualization/MapView.vue';
import PrivacyCookies from "@/components/PrivacyCookies.vue";

//Inputs
import DiscoverComp from '@/views/Visualization/SearchForm/Discover.vue';
import ForecastComp from '@/views/Visualization/SearchForm/Forecast.vue';
import ProjectionComp from '@/views/Visualization/SearchForm/Projection.vue';

//Modals
import PlotForecast from "@/views/Visualization/Modals/PlotForecast.vue";
import PlotProjection from "@/views/Visualization/Modals/PlotProjection.vue";
import PlotMonitoring from "@/views/Visualization/Modals/PlotMonitoring.vue";

import {
  disposeTooltipsByClass,
  setupTooltipsByClass,
  flagRegisteredEmail,
  flagUsePixel
} from "@/libs/Helpers";
import ModalInformation from "@/views/Visualization/Modals/Information.vue";
import AboutButton from "@/components/AboutButton.vue";
import Pixel from "@/components/Pixel.vue";
import plausible from "@/libs/PlausibleTracker";
import feedback from "@/components/Feedback.vue";

export default {
  name: 'Dashboard',
  components: {
    Pixel,
    AboutButton,
    ModalInformation,
    PrivacyCookies,
    Toast,
    Navbar,
    MapView,
    DiscoverComp,
    ForecastComp,
    ProjectionComp,
    PlotMonitoring,
    PlotForecast,
    PlotProjection
  },
  data() {
    let defaultComponent = constants.COMPONENTS.MONITORING;

    return {
      feedbackOffCanvas: {
        close: false
      },
      selectedComponent: defaultComponent, // Default component
      lastExploredComponent: defaultComponent,

      showFilterWindow: true, //show filter window
      formData: null, //Data from form
      lastFormUUID: null, //UUID from object that was last filtered in map

      // Selected layers to build plot
      plotLayers: {
        monitoring: null,
        forecast: null,
        projection: null
      },

      //List of names for selected layers (names)
      plotLayerNames: {
        monitoring: [],
        forecast: [],
        projection: []
      },

      plotLayersCountTotal: {
        monitoring: null,
        forecast: null,
        projection: null
      },

      //Trigger for plot modal
      plotDataTrigger: {
        monitoring: false,
        forecast: false,
        projection: false
      },
      // Form object with data to send to plot
      plotFormData: {
        monitoring: null,
        forecast: null,
        projection: null
      },

      plotLoading: false,

      // Modal status
      modals: {
        about: {
          status: false,
          section: constants.INFORMATION_OPTIONS.SUMMARY,
          term: null
        },
        monitoring: false,
        forecast: false,
        projection: false
      },

      tooltips: {
        about: {
          status: false,
          shown: false
        },
      },

      //Toasts
      toastSearchErrorMessage: null,
      toastSearchErrorMessageTrigger: false,

      //Constants
      constants: constants,

      //Loading
      childrenLoading: false,
      blockChildrenComponents: false,

      //Show about
      showAboutStartup: false,

    };
  },
  beforeMount() {
    /**
     * Verify that user has registered the e-mail, otherwise redirect to landing page
     */
    if (flagRegisteredEmail() === true && LoginStorage.emailRegistered === null) {
      this.$router.push({name: constants.ROUTING.LANDING, replace: true});
      return false;
    }

    /* Show or hide modal with information based on flag from local storage*/
    let labelStorage = constants.STORAGE.SHOW_VISUALIZATION_ABOUT_STARTUP;

    let showAbout = Storage.get(labelStorage);
    if (showAbout === null) {
      // No flag stored, set it as true
      Storage.set(labelStorage, true);
      this.showAboutStartup = true;
    } else {
      // Set flag from storage
      this.showAboutStartup = showAbout;
    }
    this.modals.about.status = this.showAboutStartup;

    this.tooltips.about.shown = !this.showAboutStartup

    //Show tooltip only if modal is not shown
    this.$nextTick(() => {
      this.tooltips.about.status = !this.showAboutStartup;
    })
  },
  mounted() {
    this.tooltipsSetup(); // Set up tooltips after mounting the component
    plausible.trackPageview();
  },
  updated() {
    this.tooltipsSetup(); // Set up tooltips after updating the component
  },
  beforeUnmount() {
    this.tooltipsDispose(); // Dispose tooltips when the component is about to be unmounted
  },
  beforeRouteLeave(to, from, next) {
    let modalSectionClosed = false;
    if (this.modals.about.status === true) {
      this.modals.about.status = false;
      modalSectionClosed = true;
    } else {
      let modalStatusList = ['monitoring', 'forecast', 'projection'];
      modalStatusList.forEach(section => {
        if (this.modals[section] === true) {
          this.modals[section] = false;
          modalSectionClosed = true;
          return;
        }
      });
    }

    //Force to close feedback if opened from navbar
    this.feedbackOffCanvas.close = !this.feedbackOffCanvas.close;

    if (modalSectionClosed === false)
      next(true);
    else {
      this.$nextTick(() => {
        next(true);
      })
    }
  },
  computed: {
    feedback() {
      return feedback
    },
    /**
     * Hash of components based on constant values
     * @returns {{}}
     */
    componentMap() {
      let components = {};
      components[this.constants.COMPONENTS.MONITORING] = DiscoverComp;
      components[this.constants.COMPONENTS.FORECAST] = ForecastComp;
      components[this.constants.COMPONENTS.PROJECTION] = ProjectionComp;

      return components;
    },
    currentComponent() {
      return this.componentMap[this.selectedComponent] || null;
    }
  },
  methods: {
    flagUsePixel,
    tooltipsSetup() {
      /*Tooltips for children components Discover, Forecast,
        and Projection are initialized here.*/
      this.tooltipsDispose();
      setupTooltipsByClass('.tooltip-init');
    },
    tooltipsDispose() {
      // Dispose of any existing tooltips
      disposeTooltipsByClass('.tooltip-init');
    },
    tooltipGetI18NText(componentName) {
      return this.$t(`tooltips.${componentName}`);
    },
    tooltipGlossaryOnClick(variable) {
      this.tooltips.about.status = false;
      this.aboutModalOpenOnSection(constants.INFORMATION_OPTIONS.GLOSSARY);
      this.modals.about.term = variable;
    },
    componentSelect(componentName) {
      this.selectedComponent = componentName;
      this.$nextTick(() => this.tooltipsSetup());
    },
    languageOnSwitch(locale) {
      this.$nextTick(() => this.tooltipsSetup());
    },
    filterToggle() {
      this.showFilterWindow = !this.showFilterWindow;
      if (this.showFilterWindow) {
        this.selectedComponent = this.lastExploredComponent;
        this.tooltipsSetup();
      } else {
        this.tooltipsDispose();
      }
    },
    searchHandle(data) {
      if (data !== null) {
        this.formData = data; // Store the emitted form data

        /**
         * Set UUID for last object that was searched with
         * status code 200.
         *
         * UUID will change for each instance of data class, and
         * it will be compared inside the children components to
         * block the "Explore" button to avoid the request of the
         * same visualized map.
         */
        this.lastFormUUID = data.uuid;

        // Store the last component where data was available
        this.lastExploredComponent = data.component;

        //Close about tooltip
        this.tooltips.about.status = false;

        //Close window automatically
        this.showFilterWindow = false;
      }
    },
    searchHandleError(message) {
      this.toastSearchErrorMessage = message;
      this.toastSearchErrorMessageTrigger = true;
    },
    aboutButtonOnClick() {
      this.tooltips.about.status = false;
      this.aboutModalOpenOnSection(constants.INFORMATION_OPTIONS.SUMMARY);
    },
    aboutModalOpenOnSection(section) {
      this.modals.about.section = '';
      this.$nextTick(() => {
        this.modals.about.section = section;
        this.modals.about.status = true
      });
    },
    aboutModalOnClose() {
      this.modals.about.status = false;

      if (this.tooltips.about.shown === false) {
        this.tooltips.about.status = true;
        this.tooltips.about.shown = true;
      }

      //Wait some milliseconds before clearing term
      setTimeout(() => {
        this.modals.about.term = null;
      }, 500);
    },
    searchButtonOnClickWait() {
      this.childrenLoading = true;
    },
    searchButtonAfterClickResult() {
      this.childrenLoading = false;
    },
    timeSeriesButtonOnClickReady(componentType, selectedLayerIds, selectedLayerNames, layerAreasCount) {
      let componentKey = null;
      switch (componentType) {
        case constants.COMPONENTS.MONITORING:
          componentKey = 'monitoring'
          break;
        case constants.COMPONENTS.FORECAST:
          componentKey = 'forecast'
          break;
        case constants.COMPONENTS.PROJECTION:
          componentKey = 'projection'
          break;
      }

      if (componentKey !== null) {
        //Set form data to use in modal
        this.plotFormData[componentKey] = this.formData;

        //Receive selected layers to query to API and trigger modal to open
        this.plotLoading = true;
        this.plotLayers[componentKey] = selectedLayerIds;
        this.plotLayerNames[componentKey] = selectedLayerNames;
        this.plotLayersCountTotal[componentKey] = layerAreasCount;

        this.plotDataTrigger[componentKey] = !this.plotDataTrigger[componentKey];
      }
    },
    timeSeriesDataOnLoading() {
      // Block children elements to avoid searching
      this.blockChildrenComponents = true;
    },
    timeSeriesDataAfterLoading() {
      // Clear loading in button contained in map
      this.plotLoading = false;

      // Unblock children elements to allow searching
      this.blockChildrenComponents = false;
    },
    timeSeriesDataOnError(message) {
      // Show error as alert
      this.toastSearchErrorMessage = message;
      this.toastSearchErrorMessageTrigger = true;
    },
    toastSearchErrorMessageHide() {
      this.toastSearchErrorMessageTrigger = false;
    },
    modalPlotOnOpen(modalName) {
      this.modals[modalName] = true;
    },
    modalPlotOnClose(modalName) {
      this.modals[modalName] = false;
    }
  }
};
</script>